// TimeField — shared time input primitive (J-mobile, RULE-144)
//
// Parallel to DateField. iOS Safari time inputs suffer the same intrinsic-width
// overflow as date inputs. `w-full min-w-0 box-border` baked in guarantees
// mobile-correct sizing for all consumers.
//
// L-04 primitive-level fix (Batch L revision):
// Same iOS Safari `-webkit-min-logical-width` override applies to
// `<input type="time">`. Tailwind `min-w-0` only writes `min-width: 0` and
// does NOT override `-webkit-min-logical-width`, so the time input was still
// rendering at its intrinsic content width (HH:MM AM/PM + native clock glyph)
// regardless of `w-full`. Added `appearance-none` and
// `[-webkit-min-logical-width:0]` to defeat the platform override; the
// `type="time"` attribute is preserved so the native time picker still opens
// on tap. Defense-in-depth: see `src/index.css` for matching global rules.

interface TimeFieldProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** Additional class names applied to the wrapper div */
  className?: string;
}

/**
 * Mobile-safe time input.
 *
 * - `w-full min-w-0 box-border` baked in.
 * - Pass `label` to get an accessible `<label>` above the input.
 */
export function TimeField({
  id,
  label,
  value,
  onChange,
  required,
  disabled,
  className = '',
}: TimeFieldProps) {
  return (
    <div className={`min-w-0 flex flex-col gap-1 ${className}`}>
      {label && (
        <label htmlFor={id} className="field-label">
          {label}
          {required && <span className="text-[var(--color-accent)] ml-0.5">*</span>}
        </label>
      )}
      <input
        id={id}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        // Verified on iPhone Safari 2026-04-25 — see ADR-035. iOS Safari enforces
        // -webkit-min-logical-width on native date inputs; appearance-none +
        // [-webkit-min-logical-width:0] neutralises it.
        className="w-full min-w-0 box-border appearance-none [-webkit-min-logical-width:0] rounded-lg border border-[var(--color-border)] bg-[var(--color-elevated)] text-[var(--color-text-primary)] px-3 py-2 text-body-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={id ? `time-field-${id}` : undefined}
        data-overflow-safe="true"
      />
    </div>
  );
}
